import React, { useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { useUID } from 'react-uid';

import { selectElementText } from 'utils/selectElementText';
import { translatedPlaylistName } from 'features/playlists/helpers';
import * as Types from 'types';

type Props = {
  playlist: Types.Playlist;
  canEdit?: boolean;
  onChange?: (newTitle: string) => void;
  className?: string;
};

export const PlaylistTitle: React.FC<Props> = ({
  canEdit = false,
  playlist,
  className,
  onChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [playlistNameSpan, setPlaylistNameSpan] =
    useState<null | HTMLSpanElement>(null);
  const uid = useUID();
  const initialValueRef = useRef('');

  const onBlur = useCallback(() => {
    setIsEditing(false);
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!playlistNameSpan) {
        return;
      }

      // Restore original value if user clicks escape.
      switch (event.key) {
        case 'Esc':
        case 'Escape':
          playlistNameSpan.innerHTML = initialValueRef.current;
          playlistNameSpan.blur();
          return;
        case 'Enter':
          // Do not allow new line in playlist name.
          playlistNameSpan.blur();
          event.preventDefault();
      }
    },
    [playlistNameSpan],
  );

  useEffect(() => {
    if (!playlistNameSpan) {
      return;
    }

    if (isEditing) {
      initialValueRef.current = playlistNameSpan
        ? playlistNameSpan.innerHTML
        : '';
      playlistNameSpan.addEventListener('blur', onBlur);
      document.addEventListener('keydown', onKeyDown);
      selectElementText(playlistNameSpan);
    } else {
      playlistNameSpan.removeEventListener('blur', onBlur);
      document.removeEventListener('keydown', onKeyDown);
      const newTitleValue = playlistNameSpan.innerHTML;
      if (initialValueRef.current !== newTitleValue) {
        onChange && onChange(newTitleValue);
      }
    }
  }, [isEditing, playlistNameSpan]);

  /**
   * Where do I begin... on iOS changing contentEditable="true" and opening keyboard is tricky.
   * Because we need React to commit DOM change and then execute focus. This cannot be done in
   * dummy setTimout callback because we are also doing text selection which needs to be
   * executed during onClick event.
   *
   * Solution is to do these changes after rendering, during ref setting. However we need to use
   * anonymous function to force aggressive rendering. Otherwise react would only execute it once
   * during mounting.
   */
  const playlistNameSpanCallback = useCallback(
    (node: HTMLSpanElement | null) => {
      setPlaylistNameSpan(node);
      if (node && isEditing) {
        selectElementText(node);
        node.focus();
      }
    },
    [isEditing],
  );

  const labelId = `playlist_title_${uid}`;

  return (
    <div
      className={classNames(className, {
        'playlist-title': true,
        'playlist-title--is-editing': isEditing,
        'playlist-title--can-edit': canEdit,
      })}
    >
      <span
        id={labelId}
        className='playlist-title__editable'
        ref={playlistNameSpanCallback}
        contentEditable={isEditing}
        suppressContentEditableWarning
      >
        {translatedPlaylistName(playlist)}
      </span>
      <span
        aria-labelledby={labelId}
        className='playlist-title__edit icon-edit'
        role='button'
        tabIndex={0}
        onClick={() => setIsEditing(true)}
        onKeyDown={() => setIsEditing(true)}
      />
    </div>
  );
};
